import React, { useState, useEffect } from "react";
import "./index.scss";
import { Box, TextField } from "@mui/material";
import { HTTP_METHODS, plainApi } from "../../utils/http-service";
import SiTextField from "../si-textfield";
import SiAutocomplete from "../si-autocomplete";
import { HOSTNAME, REST_URLS } from "../../utils/endpoints";
import CustomFilters from "../custom-filters";
import { ENGINE_REPLACEMENT_STATUS, TICKET_STATUS } from "./config";
import { getOfflineData } from "../../utils/offline-services";
import { USER_LIMITS, USER_ROLES } from "../../utils/constants";
import { checkForUserLimit, PAGE_LIMIT } from "../../utils";
import { TICKET_STAGES } from "../../pages/admin/ticket-details-v2/helper";

const TicketFilters = ({
  loadData,
  setMainFilters,
  filter,
  setFiltersObject,
}) => {
  const [filterType, setFilterType] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [serviceEngineerList, setServiceEngineerList] = useState([]);
  const [ticketList, setTicketList] = useState([]);
  const [oemList, setOemList] = useState([]);
  const [ticketStages, setTicketStages] = useState([]);
  const [endDate, setEndDate] = useState("");
  const [oemOrgList, setOemOrgList] = useState([]);
  let ticketTimeout = null;
  let oemTimeout = null;

  const applyFilters = (filters) => {
    if (setFiltersObject) {
      setFiltersObject(filters);
    }
    if (Object.keys(filters).length) {
      const payload = {};
      for (let key in filters) {
        if (typeof filters[key] === "object" && filters[key]?.value) {
          if (key === "ticketId") {
            payload["_id"] = filters[key].value;
          } else {
            payload[key] = filters[key].value;
          }
        } else {
          if (key === "createdAt") {
            payload["startDate"] = filters[key];
            if (endDate) {
              payload["endDate"] = endDate;
            }
          } else {
            payload[key] = filters[key];
          }
        }
      }
      if (setMainFilters) {
        setMainFilters(payload);
      }
      loadData({
        ...payload,
        sortBy: "-createdAt",
        limit: PAGE_LIMIT,
        page: filter?.page || 1,
      });
    } else {
      if (setMainFilters) {
        setMainFilters({});
      }

      loadData({
        sortBy: "-createdAt",
        limit: PAGE_LIMIT,
        page: filter?.page || 1,
      });
    }
  };

  const loadTicketOptions = async (inputValue) => {
    try {
      const response = await plainApi(
        HTTP_METHODS.GET,
        `${HOSTNAME}${REST_URLS.SEARCH_TICKET_ID}`,
        null,
        { _id: inputValue }
      );
      const { data = [] } = response || [];
      setTicketList(
        data.map((item) => ({
          label: item._id,
          value: item._id,
        }))
      );
    } catch (error) {
      console.error("Error fetching ticket options:", error);
    }
  };

  const handleTicketSearch = (newInputValue) => {
    clearTimeout(ticketTimeout);
    if (newInputValue) {
      ticketTimeout = setTimeout(() => loadTicketOptions(newInputValue), 300);
    }
  };
  const getOemList = (inputValue) => {
    const params = inputValue ? { name: inputValue } : {};
    plainApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.LIST_ADMIN_OEMS}`,
      null,
      params
    )
      .then((response) => {
        if (Array.isArray(response) && response) {
          let oems = response.map((r) => {
            return {
              ...r,
              label: `${r.code}-${r.name}`,
              value: r.id,
            };
          });

          setOemList(oems);
        }
      })
      .catch((err) => console.error(err));
  };
  const handleRegexSearch = (newInputValue) => {
    clearTimeout(oemTimeout);
    if (newInputValue) {
      oemTimeout = setTimeout(() => getOemList(newInputValue), 300);
    }
  };
  const loadServiceEngineers = () => {
    plainApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.SERVICE_ENGINEER}`,
      null,
      {
        role: "serviceEngineer",
        page: 1,
        limit: 1000,
      }
    )
      .then((response) => {
        if (response.results) {
          const result = response.results.map((ele) => ({
            ...ele,
            label: ele.name,
            value: ele.id,
          }));
          setServiceEngineerList(result);
        }
      })
      .catch((err) => console.error(err));
  };
  const getOemOrg = (filters) => {
    plainApi(HTTP_METHODS.GET, `${HOSTNAME}${REST_URLS.OEM_NAMES}`, null, {
      ...filters,
    })
      .then((response) => {
        if (response) {
          const result = response.map((ele) => ({
            label: ele.name,
            value: ele.id,
          }));
          setOemOrgList(result);
        }
      })
      .catch((err) => console.error(err));
  };
  const filterTypes = () => {
    const { role, limits } = getOfflineData("user");
    const list = [
      { label: "Filter by Ticket Id", value: "ticketId" },
      { label: "Filter by Service Engineer", value: "serviceEngineer" },
      { label: "Filter by Ticket Status", value: "status" },
      { label: "Filter by Stage", value: "stage" },
      { label: "Filter by Engine No.", value: "engineNo" },
      { label: "Filter by Date", value: "createdAt" },
    ];
    if (role !== USER_ROLES.OEM) {
      list.push({ label: "Filter by Oem Org", value: "oemOrg" });
      list.push({ label: "Filter by Oem Engineer", value: "oem" });
    }
    if (checkForUserLimit(USER_LIMITS.APPROVER, limits)) {
      list.push({
        label: "Filter by Status",
        value: "approveStatus",
      });
    }
    return list;
  };

  const renderFilterInput = () => {
    switch (filterType) {
      case "ticketId":
        return (
          <SiAutocomplete
            onChange={setFilterValue}
            options={ticketList}
            placeholder="Search ticket id"
            onInputChange={(newInputValue) => handleTicketSearch(newInputValue)}
          />
        );
      case "createdAt":
        return (
          <Box sx={{ display: "flex", gap: "20px" }}>
            <div>
              <p style={{ margin: "0px", fontWeight: 500 }}>Start Date</p>
              <SiTextField
                placeholder="Start Date"
                name="startDate"
                type="date"
                onChange={handleFilterValueChange}
                sx={{ mt: 1 }}
              />
            </div>
            <div>
              <p style={{ margin: "0px", fontWeight: 500 }}>End Date</p>
              <SiTextField
                placeholder="End Date"
                name="endDate"
                type="date"
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ mt: 1 }}
              />
            </div>
          </Box>
        );
      case "serviceEngineer":
        return (
          <SiAutocomplete
            onChange={setFilterValue}
            options={serviceEngineerList}
            placeholder="Search Service Engineer"
          />
        );
      case "stage":
        return (
          <SiAutocomplete
            onChange={setFilterValue}
            options={ticketStages}
            placeholder="Search Ticket Stages"
          />
        );
      case "oem":
        return (
          <SiAutocomplete
            onChange={setFilterValue}
            options={oemList}
            placeholder="Search Oem"
            onInputChange={(newInputValue) => handleRegexSearch(newInputValue)}
          />
        );
      case "oemOrg":
        return (
          <SiAutocomplete
            onChange={setFilterValue}
            options={oemOrgList}
            placeholder="Search Oem Org"
          />
        );
      case "status":
        return (
          <SiAutocomplete
            onChange={setFilterValue}
            options={TICKET_STATUS}
            placeholder="Ticket Status"
          />
        );
      case "approveStatus":
        return (
          <SiAutocomplete
            onChange={setFilterValue}
            options={ENGINE_REPLACEMENT_STATUS}
            placeholder="Status"
          />
        );
      case "engineNo":
        return (
          <SiTextField
            placeholder="Engine No."
            name="engineNo"
            onChange={handleFilterValueChange}
          />
        );
      default:
        return null;
    }
  };

  const handleFilterValueChange = (event) => {
    setFilterValue(event.target.value);
  };
  useEffect(() => {
    if (filterType === "serviceEngineer") {
      loadServiceEngineers();
    }
    if (filterType === "stage") {
      const arr = [];
      for (let ele in TICKET_STAGES) {
        arr.push({ label: TICKET_STAGES[ele], value: ele });
      }
      setTicketStages(arr);
    }
    if (filterType === "oem") {
      getOemList();
    }
    if (filterType === "oemOrg") {
      getOemOrg();
    }
  }, [filterType]);

  return (
    <div>
      <CustomFilters
        applyFilters={applyFilters}
        renderFilterInput={renderFilterInput}
        filterTypes={filterTypes()}
        filterType={filterType}
        setFilterType={setFilterType}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
      />
    </div>
  );
};

export default TicketFilters;
